import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
    WallBoxAddElectricalVehicleDeviceRangeModelUnitEnum,
    WallBoxUpdateElectricalVehicleRequestModel,
} from '@swagger-http';
import {
    useElectricCarBrand,
    useElectricCarChargingModesErrorAndLoading,
    useElectricCarModel,
    useHasElectricCar,
    useHasElectricCarTokenExpired,
    useIsAddressSet,
    useIsElectricCarOnline,
    useTelemetries,
    useWallboxConfigId,
} from '@store/selectors';
import { TelemetryTypes } from '@store/enums';
import {
    hideGlobalPrompt,
    setGridXStationData,
    showGlobalPrompt,
} from '@store/actions';
import EmobilitySmartChargingFlowChoicePrompt from '@containers/emobility/charging-modes/shared/smart/prompt-choose-flow';
import { EmobilitySmartChargingPrompt } from '@containers/index';
import EmobilitySmartChargingEvSettingsPrompt from './smart/prompt-ev-settings';

export const useSmartChargingPrompt = () => {
    const dispatch = useDispatch();

    const closePrompt = useCallback(() => {
        dispatch(hideGlobalPrompt());
    }, [dispatch]);

    const showSmartChargingFlowChoicePrompt = useCallback(
        (isManualTrigger?: boolean) => {
            dispatch(
                showGlobalPrompt({
                    small: true,
                    alignLeft: true,
                    onClose: closePrompt,
                    children: (
                        <EmobilitySmartChargingFlowChoicePrompt
                            isManualTrigger={isManualTrigger}
                        />
                    ),
                }),
            );
        },
        [dispatch, closePrompt],
    );

    const showEmobilityChargingPrompt = useCallback(() => {
        dispatch(
            showGlobalPrompt({
                small: true,
                alignLeft: true,
                onClose: closePrompt,
                children: (
                    <EmobilitySmartChargingPrompt
                        onSave={closePrompt}
                        onCancel={closePrompt}
                    />
                ),
            }),
        );
    }, [dispatch, closePrompt]);

    const showEmobilitySmartChargingEvSettingsPrompt = useCallback(() => {
        dispatch(
            showGlobalPrompt({
                alignLeft: true,
                onClose: closePrompt,
                children: (
                    <EmobilitySmartChargingEvSettingsPrompt
                        onSuccess={closePrompt}
                    />
                ),
            }),
        );
    }, [dispatch, closePrompt]);

    return {
        showSmartChargingFlowChoicePrompt,
        showEmobilityChargingPrompt,
        showEmobilitySmartChargingEvSettingsPrompt,
        closePrompt,
    };
};

export const useSmartCharging = () => {
    const hasElectricCar = useHasElectricCar();
    const { showSmartChargingFlowChoicePrompt, showEmobilityChargingPrompt } =
        useSmartChargingPrompt();

    const handleSmartChargingCarConnect = useCallback(
        (isManualTrigger?: boolean) => {
            if (hasElectricCar) {
                showEmobilityChargingPrompt();
                return;
            }

            showSmartChargingFlowChoicePrompt(isManualTrigger);
        },
        [
            hasElectricCar,
            showEmobilityChargingPrompt,
            showSmartChargingFlowChoicePrompt,
        ],
    );

    return { handleSmartChargingCarConnect };
};

export const useSetEvProfile = () => {
    const dispatch = useDispatch();
    const hasElectricCar = useHasElectricCar();
    const wallboxConfigId = useWallboxConfigId();

    const electricCarModel = useElectricCarModel();
    const electricCarBrand = useElectricCarBrand();

    const {
        electricCar: {
            electricCarRange: rawRange,
            electricCarBatteryLevel: rawBatteryLevel,
            electricCarBatteryCapacity: rawBatteryCapacity,
        },
    } = useTelemetries(TelemetryTypes.EMOBILITY);

    const isElectricCarBatteryLevelMissing =
        hasElectricCar && rawBatteryLevel == null;

    const estimatedRemaningRange = Math.round(rawRange ?? 0);
    const batteryLevel = rawBatteryLevel ?? 0;
    const batteryCapacity = rawBatteryCapacity ?? 0;

    const setEvProfile = useCallback(async () => {
        if (!hasElectricCar) {
            return;
        }

        if (!wallboxConfigId) throw new Error('Missing wallbox config');

        const estimatedFullRange =
            batteryLevel > 0
                ? Math.round((estimatedRemaningRange / batteryLevel) * 100)
                : 0;

        const rangeCapacity =
            estimatedFullRange > 0
                ? Math.round((batteryCapacity / estimatedFullRange) * 100)
                : 0;

        const payload: WallBoxUpdateElectricalVehicleRequestModel = {
            range: {
                value: estimatedFullRange,
                unit: WallBoxAddElectricalVehicleDeviceRangeModelUnitEnum.Km,
            },
            carType: [electricCarBrand, electricCarModel]
                .filter(Boolean)
                .join(' '),
            rangeCapacity: rangeCapacity,
            batteryCapacity: batteryCapacity,
        };

        await dispatch(setGridXStationData(wallboxConfigId, payload));
    }, [
        dispatch,
        hasElectricCar,
        wallboxConfigId,
        estimatedRemaningRange,
        batteryCapacity,
        batteryLevel,
        electricCarBrand,
        electricCarModel,
    ]);

    return { setEvProfile, isElectricCarBatteryLevelMissing };
};

export const useC2CChargingModesLockState = () => {
    const { loading } = useElectricCarChargingModesErrorAndLoading();
    const hasTokenExpired = useHasElectricCarTokenExpired();
    const isOnline = useIsElectricCarOnline();
    const isAddressSet = useIsAddressSet();

    const isLocked = loading || hasTokenExpired || !isOnline || !isAddressSet;

    return {
        isLocked,
    };
};
